import React, { useEffect, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaSearch, FaSave } from "react-icons/fa";

import {
  getVendorListByCategory,
  getVendorMenuAccess,
  updateVendorMenuAccess,
} from "../../store/Product/vendorAccessSlice";

const Product = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const formatTitleForUrl = (title) => title.toLowerCase().replace(/\s/g, '_');
  const formatTitleForDisplay = (titleParam) =>
    titleParam.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

  const {
    accessList,
    vendorList,
    loading: accessLoading,
    error: accessError,
  } = useSelector((state) => state.vendorAccess);

  const { productCategories } = useSelector((state) => state.categories);

  const activeMainCategoryData = productCategories.find(categoryItem =>
    formatTitleForUrl(categoryItem.title) === selectedCategory
  );

  // Effect to manage URL category/subcategory and set internal state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryFromUrl = params.get("category") || '';
    const subCategoryFromUrl = params.get("subCategory") || '';
    if (productCategories.length > 0 && !categoryFromUrl) {
      const defaultCategoryTitle = productCategories[0].title;
      navigate(`?category=${formatTitleForUrl(defaultCategoryTitle)}`, { replace: true });
      return; // Exit to let the navigate trigger a new render with updated URL
    }

    // Update state only if values from URL are different from current state
    if (categoryFromUrl !== selectedCategory) {
      setSelectedCategory(categoryFromUrl);
      // Reset subcategory when main category changes in URL
      setSelectedSubcategory(subCategoryFromUrl);
    } else if (subCategoryFromUrl !== selectedSubcategory) {
      // Only update subcategory if it changes, and main category is same
      setSelectedSubcategory(subCategoryFromUrl);
    }
  }, [location.search, navigate, productCategories, selectedCategory, selectedSubcategory]); // Dependencies updated

  // Effect to fetch data when selectedCategory or selectedSubcategory changes
  useEffect(() => {
    const categoryToFetch = selectedSubcategory || selectedCategory;
    console.log('categoryToFetch : ' , categoryToFetch)
    // Only fetch if a valid categoryToFetch is present and not already loading
    if (categoryToFetch && !isLoadingContent) {
      setIsLoadingContent(true);
      Promise.all([
        dispatch(getVendorListByCategory(categoryToFetch)),
        dispatch(getVendorMenuAccess({category:selectedCategory , subCategory : categoryToFetch})),
      ]).then(() => {
        setIsLoadingContent(false);
      }).catch(err => {
        console.error(err);
        setIsLoadingContent(false);
      });
    } else if (!categoryToFetch && !isLoadingContent && !accessLoading) {
      // If no category is selected at all (e.g., initial state before default is set),
      // ensure loading is off and no error is displayed prematurely.
      setIsLoadingContent(false);
    }
  }, [selectedCategory, selectedSubcategory, dispatch]); // Only trigger data fetch on these state changes

  useEffect(() => {
    if (Array.isArray(accessList) && (selectedSubcategory || selectedCategory)) {
      const vendorsWithAccess = accessList.map((vendor) => vendor._id);
      setSelectedVendors(vendorsWithAccess);
      setSelectAll(false); // Reset selectAll on new accessList
    } else {
      setSelectedVendors([]);
    }
  }, [accessList, selectedCategory, selectedSubcategory]);

  const handleVendorSelect = (vendorId) => {
    setSelectedVendors((prev) =>
      prev.includes(vendorId)
        ? prev.filter((id) => id !== vendorId)
        : [...prev, vendorId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedVendors([]);
    } else {
      setSelectedVendors(filteredVendors.map((vendor) => vendor._id));
    }
    setSelectAll(!selectAll);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const categoryToSave = selectedSubcategory || selectedCategory;
      await dispatch(
        updateVendorMenuAccess(
          {category:selectedCategory , subCategory : selectedSubcategory, vendorIds: selectedVendors })
      ).unwrap();
    } catch (error) {
      console.error("Error saving vendor access:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const filteredVendors = Array.isArray(vendorList)
    ? vendorList.filter((vendor) =>
        vendor.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  const currentModuleTitle = selectedSubcategory ? formatTitleForDisplay(selectedSubcategory) : formatTitleForDisplay(selectedCategory);

  return (
    <div className="product-wrap col-md-12 mx-auto py-3">
      {activeMainCategoryData && activeMainCategoryData.submenus && activeMainCategoryData.submenus.length > 0 && (
        <ul className="nav nav-tabs mb-4 justify-content-start">
          {activeMainCategoryData.submenus.map((submenu, subIndex) => {
            const subCategoryParam = formatTitleForUrl(submenu.title);
            const isActiveSubmenu = selectedSubcategory === subCategoryParam;
            return (
              <li className="nav-item me-2" key={subIndex}>
                <Link
                  className={`nav-link ${isActiveSubmenu ? 'active' : ''}`}
                  to={`?category=${selectedCategory}&subCategory=${subCategoryParam}`}
                >
                  {submenu.title}
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {selectedCategory || selectedSubcategory ? (
        <>
          <h2 className="mb-4"><strong>Managing Access For:</strong> {currentModuleTitle} </h2>

          <div className="filter mb-4 p-3 bg-light rounded shadow-sm">
            <div className="row w-100 align-items-center">
              <div className="col-md-6 mb-3 mb-md-0">
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <FaSearch className="text-muted" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search vendors by name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ fontSize: "1.1rem" }}
                  />
                </div>
              </div>

              <div className="col-md-6 d-flex justify-content-end align-items-center">
                <div className="form-check me-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="checkAll"
                    checked={selectAll && filteredVendors.length > 0 && selectedVendors.length === filteredVendors.length}
                    onChange={handleSelectAll}
                    style={{ transform: "scale(1.2)", marginRight: "8px" }}
                  />
                  <label
                    className="form-check-label fw-medium"
                    htmlFor="checkAll"
                    style={{ fontSize: "1.1rem", cursor: "pointer" }}
                  >
                    Select All ({filteredVendors.length})
                  </label>
                </div>

                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={isSaving || isLoadingContent || accessLoading}
                  style={{ fontSize: "1.1rem", minWidth: "120px" }}
                >
                  {isSaving ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  ) : (
                    <FaSave className="me-2" />
                  )}
                  {isSaving ? "Saving..." : "Save Access"}
                </button>
              </div>
            </div>
          </div>

          {isLoadingContent || accessLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-3">Loading vendors...</div>
            </div>
          ) : accessError ? (
            <div className="alert alert-danger text-center py-3" style={{ fontSize: "1.1rem" }}>
              Error: {accessError.message || "Failed to load data."}
            </div>
          ) : (
            <div className="product-listing mt-4">
              {filteredVendors.length === 0 ? (
                <div
                  className="alert alert-info text-center py-3"
                  style={{ fontSize: "1.1rem" }}
                >
                  {vendorList.length === 0
                    ? `No vendors found for ${currentModuleTitle}.`
                    : "No vendors match your search."}
                </div>
              ) : (
                <div className="row">
                  {[...Array(3)].map((_, colIndex) => {
                    const vendorsPerColumn = Math.ceil(filteredVendors.length / 3);
                    const startIndex = colIndex * vendorsPerColumn;
                    const endIndex = startIndex + vendorsPerColumn;
                    const columnVendors = filteredVendors.slice(
                      startIndex,
                      endIndex
                    );

                    return (
                      <div className="col-md-4" key={colIndex}>
                        {columnVendors.map((vendor) => (
                          <div
                            className="vendor-item mb-2 p-2 bg-white rounded shadow-sm d-flex align-items-center"
                            key={vendor._id}
                            style={{
                              border: "1px solid #eee",
                              transition: "all 0.2s",
                            }}
                          >
                            <div className="form-check m-0 d-flex align-items-center">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`vendor-${vendor._id}`}
                                checked={selectedVendors.includes(vendor._id)}
                                onChange={() => handleVendorSelect(vendor._id)}
                                style={{
                                  transform: "scale(1.2)",
                                  marginRight: "10px",
                                }}
                              />
                              <label
                                className="form-check-label text-truncate"
                                htmlFor={`vendor-${vendor._id}`}
                                style={{
                                  fontSize: "1.2rem",
                                  cursor: "pointer",
                                  flexGrow: 1,
                                }}
                              >
                                {vendor.name}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="alert alert-info text-center py-5" style={{ fontSize: "1.2rem" }}>
          Please select a module from the tabs above to manage vendor access.
        </div>
      )}
    </div>
  );
};

export default Product;